import { useEffect, useState } from 'react';
import { getEtiquetasDgt } from '../../../../../../helpers/carline-helpers';
import { Carline, SalesgroupWithCarline } from '../../../../../../types/vehicle-types';

function getLateralImage(option: Carline | SalesgroupWithCarline): string | undefined {
	return (
		option.mediaAssets?.D6Turntable3 ||
		option.mediaAssets?.Side_Left ||
		option.mediaAssets?.D6Explore2Alpha ||
		option?.mediaAssets?.D6Explore2Alpha ||
		option.mediaAssets?.D6MOFASideview ||
		option.mediaAssets?.D6MOFASideCV ||
		option.mediaAssets?.D6Explore2Alpha ||
		option.mediaAssets?.D6MOFASideview ||
		option.mediaAssets?.D6MOFASideCV ||
		undefined
	);
}

function getImage(option: Carline | SalesgroupWithCarline): string | undefined {
	return (
		option.mediaAssets?.D6Explore1Alpha ||
		option.mediaAssets?.D6Explore2Alpha ||
		option.mediaAssets?.D6MOFASideview ||
		undefined
	);
}

// helper: precarga una imagen nativa y notifica su estado de carga (setLoaded) -> devuelve el cleanup de los listeners
function preloadImage(src: string | undefined, setLoaded: (loaded: boolean) => void): (() => void) | undefined {
	if (!src) {
		setLoaded(false);
		return undefined;
	}

	const image = new window.Image();
	image.src = src;

	if (image.complete) {
		setLoaded(true);
		return undefined;
	}

	setLoaded(false);
	image.onload = () => setLoaded(true);
	image.onerror = () => setLoaded(false);

	return () => {
		image.onload = null;
		image.onerror = null;
	};
}

interface UseVehicleImageProps {
	option?: Carline | SalesgroupWithCarline;
	image?: string;
	lateralImage?: string;
	etiquetas?: string[];
	loadLateralImage?: boolean;
}

interface DisplayImageProps {
	lateral?: boolean;
}

export const useVehicleImage = ({
	option,
	image,
	lateralImage,
	etiquetas,
	loadLateralImage = false,
}: UseVehicleImageProps) => {
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const [isLateralImageLoaded, setIsLateralImageLoaded] = useState(false);

	const carline = option ? ('carline' in option ? option.carline : option) : undefined;

	const displayableImage = image ?? (option ? getImage(option) : undefined);
	const displayableLateralImage = loadLateralImage
		? (lateralImage ?? (option ? getLateralImage(option) : undefined))
		: undefined;

	const silhouette =
		'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-oneformv2/car_silhouette.png';
	const lateralSilhouette =
		'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-oneformv2/car_lateral_silhouette.png';

	const displayableEtiquetas = etiquetas ?? getEtiquetasDgt(carline?.etiquetasDGT ?? []);

	useEffect(() => {
		// precargamos imagen lateral y 3/4 en paralelo y limpiamos sus listeners al desmontar
		const cleanupImage = preloadImage(displayableImage, setIsImageLoaded);

		if (!loadLateralImage) {
			setIsLateralImageLoaded(false);
			return cleanupImage;
		}

		const cleanupLateralImage = preloadImage(displayableLateralImage, setIsLateralImageLoaded);

		return () => {
			cleanupImage?.();
			cleanupLateralImage?.();
		};
	}, [displayableImage, displayableLateralImage, loadLateralImage]);

	const displayImage = ({ lateral = false }: DisplayImageProps) => {
		if (lateral) {
			return displayableLateralImage && isLateralImageLoaded ? displayableLateralImage : lateralSilhouette;
		}
		return displayableImage && isImageLoaded ? displayableImage : silhouette;
	};

	return { displayImage, etiquetas: displayableEtiquetas, setIsImageLoaded, setIsLateralImageLoaded };
};
